/**
 * @file LoadingBayAdminRoutes.tsx
 * @module app/routing/LoadingBayAdminRoutes
 * @description Defines role-specific LoadingBayAdminRoutes route mappings, protecting UTrade Admin screens and selecting the appropriate application views.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { MasterLayout } from "../../_admin/layout/MasterLayout";
import TopBarProgress from "react-topbar-progress-indicator";
import { getCSSVariableValue } from "../../_admin/assets/ts/_utils";
import { WithChildren } from "../../_admin/helpers";
import AllProducts from "../modules/all-products/index";
import Orders from "../modules/orders-delivery/index";
import OrdersDelivery from "../modules/orders-delivery/pages/finance/orders-delivery-finance";
import SettingPage from "../modules/setting";
import PendingDeliveryOrders from "../modules/pending-delivery-orders/PendingDeliveryOrders";
import OutstandingReport from "../modules/outstanding-report/index";

const LoadingBayAdminRoutes = () => {
  const SellerPage = lazy(() => import("../modules/seller/index"));
  const MasterPage = lazy(() => import("../modules/master/index"));
  const ProductPage = lazy(() => import("../modules/products/index"));
  const InventoryPage = lazy(() => import("../modules/Inventory/index"));

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        <Route path="auth/*" element={<Navigate to="/orders" />} />
        <Route
          path="orders/*"
          element={
            <SuspensedView>
              <Orders />
            </SuspensedView>
          }
        />
        <Route
          path="all-products/*"
          element={
            <SuspensedView>
              <AllProducts />
            </SuspensedView>
          }
        />
        <Route
          path="products/*"
          element={
            <SuspensedView>
              <ProductPage />
            </SuspensedView>
          }
        />
        <Route
          path="orders/*"
          element={
            <SuspensedView>
              <Orders />
            </SuspensedView>
          }
        />
        <Route
          path="inventory/*"
          element={
            <SuspensedView>
              <InventoryPage />
            </SuspensedView>
          }
        />
        <Route
          path="setting/*"
          element={
            <SuspensedView>
              <SettingPage />
            </SuspensedView>
          }
        />
        <Route
          path="pending-delivery-orders/*"
          element={
            <SuspensedView>
              <PendingDeliveryOrders />
            </SuspensedView>
          }
        />
        <Route
          path="outstanding-report/*"
          element={
            <SuspensedView>
              <OutstandingReport />
            </SuspensedView>
          }
        />
      </Route>
    </Routes>
  );
};
const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--bs-primary");
  TopBarProgress.config({
    barColors: { "0": baseColor },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};
export { LoadingBayAdminRoutes };
